import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { legalApi } from './api';
import type { LegalDocument } from './documents';

/**
 * PRD §13 — generic legal acceptance modal.
 *
 * Renders a full-screen modal for any document in the catalogue. DPA gets
 * an extra signer-name + signer-title block (required by §13.4); every
 * other document uses just the bullet-checkbox flow.
 *
 * On accept:
 *   1. Each bullet must be individually checked (no master "I agree" — PRD
 *      requires separate acts).
 *   2. POST /co-legal-acceptance with versionHash. Backend is idempotent
 *      so retries don't duplicate audit rows.
 *   3. `onDone` fires after the row is written; the caller closes the modal
 *      and proceeds.
 */
export function LegalAcceptanceModal({
  document,
  brandId,
  onDone,
  onDismiss,
}: {
  document: LegalDocument;
  brandId?: number;
  onDone: () => void;
  onDismiss?: () => void;
}) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const isEs = language === 'es';
  const bullets = isEs ? document.bullets.es : document.bullets.en;
  const body = isEs ? document.body.es : document.body.en;
  const title = isEs ? document.title.es : document.title.en;

  const [checked, setChecked] = useState<boolean[]>(() =>
    bullets.map(() => false),
  );
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');

  const allChecked = checked.every(Boolean);
  const signerOk =
    !document.requiresSigner ||
    (signerName.trim().length > 1 && signerTitle.trim().length > 1);
  const canSubmit = allChecked && signerOk;

  const record = useMutation({
    mutationFn: () =>
      legalApi.record({
        documentType: document.type,
        documentVersion: document.version,
        versionHash: document.versionHash,
        brandId,
        signerName: document.requiresSigner ? signerName : undefined,
        signerTitle: document.requiresSigner ? signerTitle : undefined,
        locale: isEs ? 'es' : 'en',
      }),
    onSuccess: () => onDone(),
  });

  // DPA uses a two-pane layout (PRD §13.4: Brand obligations vs Xotski
  // obligations). Everything else is a single column.
  const twoPane = document.type === 'dpa';

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/80 px-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] text-[var(--color-xk-lavender)]">
        <header className="border-b border-[var(--color-xk-border)] px-6 py-4">
          <p className="text-[10px] uppercase tracking-widest opacity-60">
            {t('legal.eyebrow')} · {document.version}
          </p>
          <h1 className="mt-1 text-lg font-bold">{title}</h1>
        </header>

        <div
          className={`overflow-y-auto px-6 py-4 ${twoPane ? 'grid gap-4 sm:grid-cols-2' : ''}`}
        >
          {twoPane ? (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-60">
                  {t('legal.brandObligations')}
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90">{body}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-60">
                  {t('legal.xotskiObligations')}
                </p>
                <p className="mt-2 text-sm leading-relaxed opacity-90">
                  {t('legal.xotskiObligationsBody')}
                </p>
              </div>
            </>
          ) : (
            <p className="text-sm leading-relaxed opacity-90">{body}</p>
          )}
        </div>

        <div className="space-y-3 border-t border-[var(--color-xk-border)] px-6 py-4">
          {bullets.map((b, i) => (
            <label
              key={i}
              className="flex cursor-pointer items-start gap-2 text-sm leading-snug"
            >
              <input
                type="checkbox"
                checked={checked[i]}
                onChange={(e) =>
                  setChecked((arr) => {
                    const next = arr.slice();
                    next[i] = e.target.checked;
                    return next;
                  })
                }
                className="mt-0.5 h-4 w-4"
              />
              <span>{b}</span>
            </label>
          ))}

          {document.requiresSigner && (
            <div className="grid gap-3 pt-2 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-[10px] uppercase tracking-widest opacity-60">
                  {t('legal.signerName')}
                </span>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-[10px] uppercase tracking-widest opacity-60">
                  {t('legal.signerTitle')}
                </span>
                <input
                  type="text"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  className="mt-1 w-full rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2 text-sm"
                />
              </label>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 border-t border-[var(--color-xk-border)] px-6 py-4">
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-full px-4 py-2 text-sm text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
            >
              {t('legal.later')}
            </button>
          )}
          <button
            type="button"
            disabled={!canSubmit || record.isPending}
            onClick={() => record.mutate()}
            className="rounded-full bg-[var(--color-xk-accent)] px-5 py-2 text-sm font-bold text-[var(--color-xk-bg-deep)] disabled:opacity-40"
          >
            {record.isPending ? t('legal.submitting') : t('legal.accept')}
          </button>
        </footer>
      </div>
    </div>
  );
}
